import { toError } from '@fruitful/fruitils';
import { useSetAtom } from 'jotai';
import { isEmpty } from 'lodash';
import { useCallback, useState } from 'react';

import FetchError from '../../../http/FetchError';
import { captureException, captureMessage } from '../../../logger/Logger';
import { getPartialUser } from '../../../user/api/UserApi';
import { useUser } from '../../../user/providers/UserProvider';
import {
    SESSION_DURATION_MINUTES,
    STYTCH_ERROR_MESSAGE_BY_TYPE,
} from '../../constants';
import { storeUserToken } from '../../helpers';
import { isUserSetupInProgressAtom } from '../../state/AuthAtoms';
import {
    checkPasswordStrength,
    getStytchErrorMessage,
} from '../../stytchHelpers';
import type { PasswordRequirementsFeedback } from '../../types/Auth';
import { useStytch } from '../useAuth';

const parseError = (error: Error) => {
    return new Error(getStytchErrorMessage(error));
};

// A duplicate_email Stytch identity can mean two very different things:
// (a) an abandoned signup — Stytch account created, but "Tell us more about
//     you" never finished, so there's no Fruitful backend user yet, or
// (b) a genuine returning member with a real completed account.
// Only (a) should be allowed to silently continue just because their
// newly-typed password happens to match. Checked by stytchId, not password,
// so the outcome doesn't depend on which password they guessed.
const hasExistingFruitfulUser = async (stytchId: string): Promise<boolean> => {
    try {
        const user = await getPartialUser({ stytchId });
        return !(user == null || isEmpty(user));
    } catch (error) {
        if (error instanceof FetchError && error.status === 403) {
            return false;
        }
        return true;
    }
};

type SuccessHandler = (
    userId: string,
    email: string,
    sessionId: string,
) => void;
type ErrorHandler = (
    error: Error,
    passwordFeedback?: PasswordRequirementsFeedback,
) => void;

interface CreateStytchAccountParams {
    email: string;
    password: string;
    onSuccess: SuccessHandler;
    onError: ErrorHandler;
}

export const useCreateStytchAccount = () => {
    const stytch = useStytch();
    const { user } = useUser();
    const [isCreatingStytchAccount, setIsCreatingStytchAccount] =
        useState(false);
    const setIsUserSetupInProgress = useSetAtom(isUserSetupInProgressAtom);

    const createStytchAccount = useCallback(
        async ({
            email,
            password,
            onSuccess,
            onError,
        }: CreateStytchAccountParams) => {
            setIsCreatingStytchAccount(true);

            const onStytchAccountCreationSuccess: SuccessHandler = (
                userId,
                email,
                sessionId,
            ) => {
                captureMessage('Authentication account creation successful');
                setIsCreatingStytchAccount(false);
                onSuccess?.(userId, email, sessionId);
            };
            const onStytchAccountCreationError: ErrorHandler = (
                error,
                passwordFeedback,
            ) => {
                setIsCreatingStytchAccount(false);
                onError?.(error, passwordFeedback);
            };

            try {
                if (user) {
                    throw new Error(
                        'Cannot create new stytch account if user is already logged in.',
                    );
                }

                const checkPasswordStrengthResponse =
                    await checkPasswordStrength(password);
                if (!checkPasswordStrengthResponse.validPassword) {
                    const { message, strengthCheck } =
                        checkPasswordStrengthResponse;
                    onStytchAccountCreationError(
                        new Error(message),
                        strengthCheck,
                    );
                    return;
                }

                const {
                    user: stytchUser,
                    session_jwt,
                    session,
                } = await stytch.passwords.create({
                    email,
                    password,
                    session_duration_minutes: SESSION_DURATION_MINUTES,
                });

                storeUserToken(session_jwt);

                onStytchAccountCreationSuccess(
                    stytchUser.user_id,
                    stytchUser.emails?.[0]?.email ?? email,
                    session.session_id,
                );
            } catch (error) {
                const err = parseError(toError(error));

                if (
                    err.message === STYTCH_ERROR_MESSAGE_BY_TYPE.duplicate_email
                ) {
                    // authenticate() below logs the user in as a side
                    // effect. Suppress UserProvider until we know whether
                    // to keep or revoke that session, or it can race ahead
                    // and redirect first.
                    setIsUserSetupInProgress(true);

                    // A wrong password guess against an existing account
                    // rejects (e.g. 401) rather than resolving with a
                    // non-200 status — handle both shapes the same way.
                    let authenticateResponse: Awaited<
                        ReturnType<typeof stytch.passwords.authenticate>
                    > | null = null;
                    try {
                        authenticateResponse =
                            await stytch.passwords.authenticate({
                                email,
                                password,
                                session_duration_minutes:
                                    SESSION_DURATION_MINUTES,
                            });
                    } catch (authenticateError) {
                        captureException(toError(authenticateError));
                    }

                    const authenticatedResponse =
                        authenticateResponse?.status_code === 200
                            ? authenticateResponse
                            : null;
                    const isReturningMember =
                        authenticatedResponse != null &&
                        (await hasExistingFruitfulUser(
                            authenticatedResponse.user_id,
                        ));

                    if (authenticatedResponse != null && !isReturningMember) {
                        // Abandoned signup — no Fruitful user yet, safe to continue.
                        onStytchAccountCreationSuccess(
                            authenticatedResponse.user_id,
                            authenticatedResponse.user.emails?.[0]?.email ??
                                email,
                            authenticatedResponse.session.session_id,
                        );
                    } else {
                        if (authenticatedResponse != null) {
                            // A genuine returning member — the authenticate
                            // call above just established a session for
                            // their old identity as a side effect. Revoke it
                            // immediately; they need to go through account
                            // reactivation (below), not be silently logged
                            // into their stale account.
                            try {
                                await stytch.session.revoke();
                            } catch (revokeError) {
                                captureException(toError(revokeError));
                            }
                        }
                        setIsUserSetupInProgress(false);
                        captureException(err);
                        onStytchAccountCreationError(err);
                    }
                } else {
                    captureException(err);
                    onStytchAccountCreationError(err);
                }
            }
        },
        [user, setIsUserSetupInProgress],
    );

    return {
        createStytchAccount,
        isCreatingStytchAccount,
    };
};
